import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { store } from 'redux/store';
import {
  setGroupId,
  setLocationId,
  setCulture,
  setForecastDate,
  setPeriodStart,
  setPeriodEnd,
  setStage,
  selectMeteorologyAgroCultureIndexLocationTriggerRequest,
} from 'redux/reducers/meteorologyAgroCultureIndexLocationReducer';

/**
 * Carrega automaticamente os dados quando a tela é aberta a partir do clique num
 * pin do mapa de Área (`navigate(state)`).
 *
 * IMPORTANTE: os setters do Redux são aplicados **uma única vez por navegação**
 * (effect com dep só em `location.state`) e o auto-disparo da consulta também é
 * guardado por ref. Sem isso, como `triggerRequest` ganha uma nova referência a
 * cada mudança de params (o `useCharts` re-registra o trigger sempre que os
 * filtros mudam), o effect re-aplicaria o `state` original e reverteria qualquer
 * filtro que o usuário editasse depois (ex.: escolher um Estágio fenológico).
 */
export default function useAutoLoad() {
  const location = useLocation();
  const triggerRequest = useSelector(selectMeteorologyAgroCultureIndexLocationTriggerRequest);
  const autoLoadedStateRef = useRef<unknown>(null);

  // 1) Popula o Redux com os dados vindos da navegação — apenas quando o state muda.
  useEffect(() => {
    const state = location.state as any;
    if (state && state.groupId && state.locationId && state.culture) {
      store.dispatch(setGroupId(state.groupId));
      store.dispatch(setLocationId(state.locationId));
      store.dispatch(setCulture(state.culture));
      store.dispatch(setForecastDate(state.forecastDate));
      store.dispatch(setPeriodStart(state.periodStart));
      store.dispatch(setPeriodEnd(state.periodEnd));
      store.dispatch(setStage(state.stage));
    }
  }, [location.state]);

  // 2) Dispara a consulta uma única vez, quando o trigger ficar disponível.
  useEffect(() => {
    const state = location.state as any;
    if (
      triggerRequest
      && state && state.groupId && state.locationId && state.culture
      && autoLoadedStateRef.current !== state
    ) {
      autoLoadedStateRef.current = state;
      const timer = setTimeout(() => triggerRequest(), 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [triggerRequest, location.state]);
}
